import { ChangeDetectorRef, DestroyRef, Directive, OnInit, effect, inject, input, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { LocalStorageService } from '@df/browser/storage/local-storage.service';
import { NCatalogUi } from '@df/catalog-ui/df-catalog-ui.config';
import { IListingSizeOption } from '@df/catalog-ui/listing/size/listing-size';
import { TNumberPerSize } from '@df/core/df-core.interfaces';
import { Meta } from '@df/core/meta/meta';
import { Client } from '@df/core/service/client';
import { Logger } from '@df/core/service/logger';
import { TimeoutService } from '@df/ng/timeout.service';
import { getState } from '@ngrx/signals';
import { filter } from 'rxjs/operators';
import { AdvancedCommerce } from '../advanced-commerce.namespace';
import { AdvancedCommerceService } from '../advanced-commerce.service';
import { AdvancedCommerceSearchResult } from '../search/advanced-commerce-search-result';
import { AdvancedCommerceConfigStore } from '../store/advanced-commerce-config.store';
import { AdvancedCommerceListingStore } from '../store/advanced-commerce-listing.store';
import { AdvancedCommerceSearchStore } from '../store/advanced-commerce-search.store';

@Directive()
export class AdvancedCommerceListingComponentDef implements OnInit {
    // override element from iCMS configuration
    readonly element = input<AdvancedCommerce.Query.Element>();
    readonly result = input<AdvancedCommerceSearchResult>();

    private readonly listingSizeStorageId = 'listingSize';
    private currentSize: TNumberPerSize = [0, 0, 0, 0];

    readonly store = inject(AdvancedCommerceListingStore);
    readonly storeSearch = inject(AdvancedCommerceSearchStore);
    readonly storeConfig = inject(AdvancedCommerceConfigStore);

    protected client = inject(Client);
    protected logger = inject(Logger);
    protected meta = inject(Meta);
    protected timeoutService = inject(TimeoutService);
    protected router = inject(Router);
    protected changeDetectorRef = inject(ChangeDetectorRef);
    protected destroyRef = inject(DestroyRef);
    protected advancedCommerceService = inject(AdvancedCommerceService);
    protected moduleConfig = inject(NCatalogUi.OPTIONS);
    protected localStorageService = inject(LocalStorageService);

    get defaultElement() {
        const element = this.element();

        if (element) {
            return element;
        }

        const config = this.storeConfig.config();
        const metaModel = this.meta.model;

        if (config?.customCategoriesId?.length && metaModel?.type === 'category' && metaModel?.id) {
            return (
                config?.customCategoriesId?.find(category => category.categories.includes(metaModel.id as number))?.id ||
                config?.defaultCategoryId
            );
        } else {
            return config?.defaultCategoryId || AdvancedCommerce.Query.Element.category;
        }
    }

    constructor() {
        this.readStorage();

        // hack for detect changes in signals store
        effect(() => {
            // read store changes and update template
            const state = getState(this.store);
            this.logger.info('AC listing state:', state);
            this.saveStorage();
            this.changeDetectorRef.detectChanges();
        });

        effect(() => {
            const result = this.result();

            untracked(() => {
                if (this.meta?.model?.type !== 'category') {
                    this.store.elementSet(this.element() || AdvancedCommerce.Query.Element.search);
                    // update listing store response with data from search store
                    this.store.responseSet(this.storeSearch.response());
                }
            });
        });
    }

    ngOnInit(): void {
        // set up element on listing init
        this.store.elementSet(this.defaultElement);
        // data for search is already loaded from search resolver
        if (this.element() !== AdvancedCommerce.Query.Element.search) {
            // load data
            this.updateData();
        } else {
            this.store.elementSet(this.element() || AdvancedCommerce.Query.Element.search);
            // update listing store response with data from search store
            this.store.responseSet(this.storeSearch.response());
        }
        // watch for route change to update data
        this.router.events
            .pipe(
                filter(state => state instanceof NavigationEnd),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(state => {
                this.timeoutService.setTimeout(() => {
                    if (this.meta?.model?.type === 'category') {
                        this.store.elementSet(this.defaultElement);

                        this.updateData();
                    }
                }, 100);
            });

        // set default listing size
        this.syncOptions();
        this.client.obs.breakpoint.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.syncOptions());
    }

    protected updateData(): void {
        const data: Partial<AdvancedCommerce.Query.RequestData> = {
            elements: [this.defaultElement]
        };

        if (this.store.metaIsPagingRequest()) {
            data.is_paging_request = true;
        }

        // if there was search request then we should send `search_text` in context
        const term = this.result()?.term || this.storeSearch.searchRequest()?.q;

        if (term) {
            data.context = {
                search_text: term
            };
        }

        this.store.dataSet(data);
    }

    protected readStorage(): void {
        const storageValue = this.localStorageService.get(this.listingSizeStorageId);

        if (Array.isArray(storageValue) && storageValue.length === 4) {
            this.currentSize = storageValue.map(i => Number(i) || 0) as TNumberPerSize;
        }
    }

    protected saveStorage(): void {
        const clientSizeId = this.client.sizeId;
        this.currentSize[clientSizeId] = this.store.listingCurrentSize();

        this.localStorageService.set(this.listingSizeStorageId, this.currentSize);
    }

    protected syncOptions() {
        const listingSize = this.getListingSize();

        if (this.store.listingCurrentSize() !== listingSize) {
            this.store.setListingSize(listingSize);
        }
    }

    protected getListingSize(): number {
        const clientSizeName = this.client.size;
        const clientSizeId = this.client.sizeId;
        const options = this.moduleConfig.listing.category.size?.filter(i => i.onSize?.includes(clientSizeName));
        const defaultSize = this.moduleConfig.listing.category.defaultSize;

        const option =
            this.toListingSizeOption(this.currentSize[clientSizeId], options) ||
            (defaultSize && this.toListingSizeOption(defaultSize[clientSizeId], options)) ||
            options?.[0];

        return option?.size || 1;
    }

    protected toListingSizeOption(option: number | IListingSizeOption, options?: IListingSizeOption[]): IListingSizeOption | undefined {
        const size = typeof option === 'number' ? option : option.size;

        return options?.find(option => option.size == size);
    }
}
